/* eslint-disable max-len */
import React, { useState } from 'react';
import { CountryDropdown, RegionDropdown } from 'react-country-region-selector';
import { Field, Form, FormikProvider, useFormik } from 'formik';
import { bool } from 'prop-types';
import * as Yup from 'yup';
import { STATUS_FAILURE, STATUS_INIT, STATUS_SUCCESS } from '../../common/constants/formikStatus.constant';
import { consentChoiceValue } from '../../common/constants/privacyPolicyPopup.constant';
import ArrowRightIcon from '../../icons/arrow-RCerulean.svg';
import RotatedArrow from '../../icons/arrow-rotated.svg';
import PdfIcon from '../../icons/pdf-icon.svg';
import PrivacyPolicyConfirmationPopup from '../PrivacyPolicy/PrivacyPolicyConfirmationPopup';
import { dataLayerBusinessContactFormSubmit } from '../../utils/dataLayerEvents';

const formValidationSchema = Yup.object().shape({
  email: Yup.string().email('Invalid email').required('Required'),
  firstName: Yup.string().required('Required'),
  lastName: Yup.string().required('Required'),
  title: Yup.string().required('Required'),
  industry: Yup.string().required('Required'),
  phoneNumber: Yup.string().required('Required'),
  country: Yup.string().required('Required'),
  state: Yup.string().required('Required'),
});

const ContactForm = (props) => {
  const { gated = false } = props;

  const [formStatusType, setFormStatusType] = useState(STATUS_INIT);
  const [openPrivacyPolicyPopup, setOpenPrivacyPolicyPopup] = useState(false);

  const formik = useFormik({
    initialStatus: STATUS_INIT,
    initialValues: {
      email: '',
      phoneNumber: '',
      firstName: '',
      lastName: '',
      title: '',
      industry: '',
      country: '',
      state: '',
      additionalInformation: '',
    },
    validationSchema: formValidationSchema,
    onSubmit: (values, actions) => {
      if (!values.consent) {
        setOpenPrivacyPolicyPopup(true);
        return;
      }
      setOpenPrivacyPolicyPopup(false);

      const { resetForm, setSubmitting } = actions;

      const data = {
        fields: [
          {
            name: 'email',
            value: values.email,
          },
          {
            name: 'phone',
            value: values.phoneNumber,
          },
          {
            name: 'firstname',
            value: values.firstName,
          },
          {
            name: 'lastname',
            value: values.lastName,
          },
          {
            name: 'jobtitle',
            value: values.title,
          },
          {
            name: 'industry',
            value: values.industry,
          },
          {
            name: 'country',
            value: values.country,
          },
          {
            name: 'state',
            value: values.state,
          },
          {
            name: 'state',
            value: values.state,
          },
          {
            name: 'website_inbound_message',
            value: values.additionalInformation,
          },
        ],
        context: {
          pageName: document.title ?? '',
          pageUri: window.location?.href ?? '',
        },
      };

      // Add hubspot tracking cookie if available
      const hubspotTrackingCookie = document.cookie.replace(/(?:(?:^|.*;\s*)hubspotutk\s*=\s*([^;]*).*$)|^.*$/, '$1');

      if (hubspotTrackingCookie !== '') data.context.hutk = hubspotTrackingCookie;

      // submission URL
      const url = gated
        ? 'https://api.hsforms.com/submissions/v3/integration/submit/6286664/a3ae9415-4afc-4048-9b4f-1547cb06dc7b'
        : 'https://api.hsforms.com/submissions/v3/integration/submit/6286664/64f1ab85-e449-42a7-a483-ddac8811f5d5';

      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })
        .then((response) => {
          // response.ok checks if the response.status is in the range 200-299, inclusive
          setFormStatusType(response.ok ? STATUS_SUCCESS : STATUS_FAILURE);
        })
        .then(() => {
          // Clear selected fields after form submits
          resetForm();
        })
        .catch(() => {
          setFormStatusType(STATUS_FAILURE);
        })
        .finally(() => {
          resetForm({
            values: {
              consent: false,
            },
          });
          setSubmitting(false);

          dataLayerBusinessContactFormSubmit();
        });
    },
  });

  const onChoosingConsent = (choice) => {
    if (choice === consentChoiceValue.consent) {
      formik.values.consent = choice;
      formik.handleSubmit();
    } else {
      setOpenPrivacyPolicyPopup(false);
      formik.setSubmitting(false);
    }
  };

  return (
    <div className="bg-none text-white">
      {/* form default state */}
      {formStatusType !== STATUS_SUCCESS && (
        <>
          <FormikProvider value={formik}>
            {/* {({ errors, touched, values, handleChange, handleBlur }) => ( */}
            <Form
              className="grid font-brown text-[12px] grid-cols-1 md:grid-cols-2 gap-y-10 md:gap-y-15 md:gap-x-32
                max-w-[980px] py-20"
            >
              <div className="flex flex-col col-span-2 md:col-span-1">
                <div className="flex-row flex items-center mb-[13px]">
                  <span className="text-cerulean-400 uppercase tracking-[2.88px]">E-mail*</span>
                  {formik.errors.email && formik.touched.email ? (
                    <span className="text-red-600 ml-3 text-[12px] uppercase">{formik.errors.email}</span>
                  ) : null}
                </div>
                <Field className="bg-[#717F84] p-3 max-h-[32px] rounded-[4px]" name="email" type="email" />
              </div>

              <div className="flex flex-col col-span-2 md:col-span-1">
                <div className="flex-row flex items-center mb-[13px]">
                  <span className="text-cerulean-400 uppercase tracking-[2.88px]">Phone number*</span>
                  {formik.errors.phoneNumber && formik.touched.phoneNumber ? (
                    <span className="text-red-600 ml-3 text-[12px] uppercase">{formik.errors.phoneNumber}</span>
                  ) : null}
                </div>
                <Field className="bg-[#717F84] max-h-[32px] p-3 rounded-[4px]" name="phoneNumber" type="tel" />
              </div>

              <div className="flex flex-col col-span-2 md:col-span-1">
                <div className="flex-row flex items-center mb-[13px]">
                  <span className="text-cerulean-400 uppercase tracking-[2.88px]">First Name*</span>
                  {formik.errors.firstName && formik.touched.firstName ? (
                    <span className="text-red-600 ml-3 text-[12px] uppercase">{formik.errors.firstName}</span>
                  ) : null}
                </div>
                <Field className="bg-[#717F84] max-h-[32px] p-3 rounded-[4px] text-[12px]" name="firstName" />
              </div>

              <div className="flex flex-col col-span-2 md:col-span-1">
                <div className="flex-row flex items-center mb-[13px]">
                  <span className="text-cerulean-400 uppercase tracking-[2.88px]">Last Name*</span>
                  {formik.errors.lastName && formik.touched.lastName ? (
                    <span className="text-red-600 ml-3 text-[12px] uppercase">{formik.errors.lastName}</span>
                  ) : null}
                </div>
                <Field className="bg-[#717F84] max-h-[32px] p-3 rounded-[4px]" name="lastName" />
              </div>

              <div className="flex flex-col col-span-2 md:col-span-1">
                <div className="flex-row flex items-center mb-[13px]">
                  <span className="text-cerulean-400 uppercase tracking-[2.88px]">Industry*</span>
                  {formik.errors.industry && formik.touched.industry ? (
                    <span className="text-red-600 ml-3 text-[12px] uppercase">{formik.errors.industry}</span>
                  ) : null}
                </div>
                <Field className="bg-[#717F84] p-3 max-h-[32px] rounded-[4px]" name="industry" />
              </div>

              <div className="flex flex-col col-span-2 md:col-span-1">
                <div className="flex-row flex items-center mb-[13px]">
                  <span className="text-cerulean-400 uppercase tracking-[2.88px]">Title*</span>
                  {formik.errors.title && formik.touched.title ? (
                    <span className="text-red-600 ml-3 text-[12px] uppercase">{formik.errors.title}</span>
                  ) : null}
                </div>
                <Field className="bg-[#717F84] max-h-[32px] p-3 rounded-[4px]" name="title" />
              </div>

              <div className="flex flex-col col-span-2 md:col-span-1">
                <div className="flex-row flex items-center mb-[13px]">
                  <span className="text-cerulean-400 uppercase tracking-[2.88px] mb-[13px]">Country*</span>
                  {formik.errors.country && formik.touched.country ? (
                    <span className="text-red-600 ml-3 text-[12px] uppercase">{formik.errors.country}</span>
                  ) : null}
                </div>
                <div className="relative">
                  <CountryDropdown
                    name="country"
                    className="bg-[#717F84] text-white max-h-[32px] h-[32px] p-2 pl-3 rounded-[4px] w-full
                      appearance-none"
                    value={formik.values.country}
                    onChange={(_, e) => formik.handleChange(e)}
                    onBlur={formik.handleBlur}
                    priorityOptions={['US']}
                  />
                  <div
                    className="absolute top-0 right-0 text-white bg-[#919794] py-4 px-3 rounded-tr-[4px]
                      rounded-br-[4px] h-full flex items-center"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="11" viewBox="0 0 20 11" fill="none">
                      <path
                        /* eslint-disable-next-line max-len */
                        d="M10.62 10.0096L10.6207 10.0089L18.9814 2.211C19.0653 2.13273 19.1329 2.03885 19.1793 1.93424C19.2258 1.82959 19.25 1.71673 19.25 1.60229C19.25 1.48785 19.2258 1.37499 19.1793 1.27033C19.1329 1.16573 19.0653 1.07185 18.9814 0.993582L18.9814 0.993585C18.8975 0.915346 18.7988 0.85413 18.6915 0.812645C18.5841 0.771165 18.4695 0.75 18.3542 0.75C18.2389 0.75 18.1244 0.771165 18.017 0.812644C17.9096 0.854131 17.811 0.915347 17.7271 0.993585L17.727 0.993653L9.99357 8.21239L2.27297 1.00565L2.2729 1.00558C2.10379 0.847854 1.87793 0.761996 1.64576 0.761996C1.41359 0.761996 1.18773 0.847854 1.01862 1.00558C0.848924 1.16386 0.75 1.3824 0.75 1.61429C0.75 1.84617 0.848924 2.06472 1.01862 2.22299L9.37929 10.0209L9.3792 10.021L9.38537 10.0263C9.55276 10.1725 9.7706 10.251 9.99366 10.25C10.1084 10.2504 10.2224 10.2299 10.3295 10.1891L10.2405 9.95546L10.3295 10.1891C10.437 10.1481 10.5358 10.0874 10.62 10.0096Z"
                        fill="#DFDCD2"
                        stroke="#DFDCD2"
                        strokeWidth="0.5"
                      />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="flex flex-col col-span-2 md:col-span-1">
                <div className="flex-row flex items-center mb-[13px]">
                  <span className="text-cerulean-400 uppercase tracking-[2.88px] mb-[13px]">State*</span>
                  {formik.errors.state && formik.touched.state ? (
                    <span className="text-red-600 ml-3 text-[12px] uppercase">{formik.errors.state}</span>
                  ) : null}
                </div>
                <div className="relative">
                  <RegionDropdown
                    name="state"
                    defaultOptionLabel="Select a state"
                    className="bg-[#717F84] text-white w-full max-h-[32px] h-[32px] p-2 pl-3 rounded-[4px]
                      appearance-none"
                    country={formik.values.country}
                    value={formik.values.state}
                    onChange={(_, e) => formik.handleChange(e)}
                    onBlur={formik.handleBlur}
                  />
                  <div
                    className="absolute top-0 right-0 text-white bg-[#919794] py-4 px-3 rounded-tr-[4px]
                      rounded-br-[4px] h-full flex items-center"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="11" viewBox="0 0 20 11" fill="none">
                      <path
                        /* eslint-disable-next-line max-len */
                        d="M10.62 10.0096L10.6207 10.0089L18.9814 2.211C19.0653 2.13273 19.1329 2.03885 19.1793 1.93424C19.2258 1.82959 19.25 1.71673 19.25 1.60229C19.25 1.48785 19.2258 1.37499 19.1793 1.27033C19.1329 1.16573 19.0653 1.07185 18.9814 0.993582L18.9814 0.993585C18.8975 0.915346 18.7988 0.85413 18.6915 0.812645C18.5841 0.771165 18.4695 0.75 18.3542 0.75C18.2389 0.75 18.1244 0.771165 18.017 0.812644C17.9096 0.854131 17.811 0.915347 17.7271 0.993585L17.727 0.993653L9.99357 8.21239L2.27297 1.00565L2.2729 1.00558C2.10379 0.847854 1.87793 0.761996 1.64576 0.761996C1.41359 0.761996 1.18773 0.847854 1.01862 1.00558C0.848924 1.16386 0.75 1.3824 0.75 1.61429C0.75 1.84617 0.848924 2.06472 1.01862 2.22299L9.37929 10.0209L9.3792 10.021L9.38537 10.0263C9.55276 10.1725 9.7706 10.251 9.99366 10.25C10.1084 10.2504 10.2224 10.2299 10.3295 10.1891L10.2405 9.95546L10.3295 10.1891C10.437 10.1481 10.5358 10.0874 10.62 10.0096Z"
                        fill="#DFDCD2"
                        stroke="#DFDCD2"
                        strokeWidth="0.5"
                      />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="flex flex-col col-span-2">
                <span className="text-cerulean-400 uppercase tracking-[2.88px] mb-[13px]">Additional Information*</span>
                <Field
                  className="bg-[#717F84] p-3 col-span-2 rounded-[4px] min-h-52"
                  name="additionalInformation"
                  as="textarea"
                />
              </div>

              <button
                type="submit"
                className="bg-orange-400 tracking-[2.16px] rounded-[48px] hover:bg-orange-700 text-white uppercase py-3
                  px-4 text-[12px] mt-16 max-w-40"
              >
                Get in Touch
                <span className="min-h-10 min-w-10 max-w-10">
                  <RotatedArrow className="mt-[-2px]" />
                </span>
              </button>
            </Form>
            {/* )} */}
          </FormikProvider>
          {openPrivacyPolicyPopup && <PrivacyPolicyConfirmationPopup onPopUpClose={onChoosingConsent} />}
        </>
      )}

      {/* form success state, ungated contact form */}
      {formStatusType === STATUS_SUCCESS && !gated && (
        <div className="pt-20 pb-64">
          <p className="text-[18px] font-brown text-beige-300 tracking-[1.8px]">Thanks for submitting the form.</p>
        </div>
      )}

      {/* form success state, for gated pdf form */}
      {formStatusType === STATUS_SUCCESS && gated && (
        <div className="pt-20 pb-64">
          <p className="text-[18px] font-brown text-beige-300 tracking-[1.8px]">
            Thanks for submitting the form. Click below to download the kilo product sheet.
          </p>
          <p className="pt-4">
            <a
              className="decoration-0 text-cerulean-400 text-[10px] uppercase tracking-[2.16px]"
              href="https://6286664.fs1.hubspotusercontent-na1.net/hubfs/6286664/PFF%20site/kilo_product-sheet.pdf"
            >
              <PdfIcon className="mr-3 inline-block" />
              <span className="font-bold">download product sheet</span>
              <ArrowRightIcon className="ml-3 inline-block" />
            </a>
          </p>
        </div>
      )}
    </div>
  );
};

ContactForm.propTypes = {
  gated: bool,
};

export default ContactForm;
